import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import {
  Button,
  Icon,
  Loading,
  NavList,
  NavListItem,
  NavListSection,
  Pane,
  PaneMenu,
  Paneset,
} from '@folio/stripes-components';
import { CalloutContext, useOkapiKy } from '@folio/stripes/core';
import LanguagesTranslatorsDetail from './LanguagesTranslatorsDetail';
import AssignTranslatorModal from './AssignTranslatorModal';
import { getLocaleLabel, ImportLocalesIcones } from '../../utils/utils';

function LanguagesTranslatorsManager({ match, history, location }) {
  const intl = useIntl();
  const flags = ImportLocalesIcones();
  const okapiKy = useOkapiKy();
  const callout = useContext(CalloutContext);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [usersMap, setUsersMap] = useState({});

  const { data, isLoading, refetch } = useQuery(
    'languageTranslators',
    () => okapiKy.get('languageTranslators?limit=1000&query=cql.allRecords=1').json(),
    { refetchOnWindowFocus: false },
  );

  const entryList = sortBy(data?.languageTranslators || [], ['localeValue']);

  // Fetch all translator user details once when data loads
  useEffect(() => {
    if (!entryList.length) return;

    const allIds = [...new Set(
      entryList.flatMap((r) => r.translators || [])
        .filter((id) => !usersMap[id])
    )];

    if (!allIds.length) return;

    const cql = allIds.map((id) => `id=="${id}"`).join(' or ');
    okapiKy.get(`users?query=${encodeURIComponent(cql)}&limit=1000`).json()
      .then((res) => {
        const fetched = {};
        (res.users || []).forEach((u) => { fetched[u.id] = u; });
        setUsersMap((prev) => ({ ...prev, ...fetched }));
      })
      .catch(() => {});
  }, [data]);

  const localeFromUrl = location.pathname
    .replace(match.path, '').replace(/^\//, '');

  const selectedRecord = localeFromUrl
    ? entryList.find((r) => r.localeValue === localeFromUrl) || null
    : null;

  const handleSelect = (record) => {
    history.push(`${match.path}/${record.localeValue}`);
  };

  const handleClose = () => history.push(match.path);

  const handleSave = async (updatedUser) => {
    // If a new user was added, update usersMap immediately
    if (updatedUser) {
      setUsersMap((prev) => ({ ...prev, [updatedUser.id]: updatedUser }));
    }
    await refetch();
  };

  const handleAssignSave = async (users, localeValues) => {
    if (!users?.length || !localeValues.length) return;

    try {
      const result = await okapiKy.post('languageTranslators/assign', {
        json: {
          translatorIds: users.map((u) => u.id),
          localeValues,
        },
      }).json();

      // Update usersMap with newly assigned users
      const newMap = {};
      users.forEach((u) => { newMap[u.id] = u; });
      setUsersMap((prev) => ({ ...prev, ...newMap }));

      await refetch();

      callout?.sendCallout({
        type: 'success',
        message: (
          <FormattedMessage
            id="ui-translations.settings.languagesTranslators.assignModal.success"
            defaultMessage="{count} translator(s) assigned to {locales} language(s)"
            values={{
              count: users.length,
              locales: result.updated?.length || localeValues.length,
            }}
          />
        ),
      });
    } catch (e) {
      let errorMessage = 'Assignment failed';
      try {
        const err = await e.response?.json();
        errorMessage = err?.errors?.[0]?.message || errorMessage;
      } catch (_) { /* ignore */ }
      callout?.sendCallout({ type: 'error', message: errorMessage });
    }
  };

  const renderLastMenu = () => (
    <PaneMenu>
      <Button
        buttonStyle="primary"
        marginBottom0
        onClick={() => setShowAssignModal(true)}
      >
        <Icon icon="plus-sign" />
        {' '}
        <FormattedMessage
          id="ui-translations.settings.languagesTranslators.assignModal.trigger"
          defaultMessage="Assign Translator"
        />
      </Button>
    </PaneMenu>
  );

  return (
    <Paneset>
      <Pane
        defaultWidth={selectedRecord ? '30%' : 'fill'}
        paneTitle={
          <FormattedMessage id="ui-translations.settings.languagesTranslators.lable" />
        }
        paneSub={
          <FormattedMessage
            id="ui-translations.settings.languages.pane.subHeader"
            values={{ count: entryList.length }}
          />
        }
        lastMenu={renderLastMenu()}
      >
        {isLoading ? (
          <Loading />
        ) : (
          <NavList>
            <NavListSection
              activeLink={`${match.path}/${selectedRecord?.localeValue}`}
            >
              {entryList.map((record) => (
                <NavListItem
                  key={record.id}
                  to={`${match.path}/${record.localeValue}`}
                  onClick={() => handleSelect(record)}
                >
                  <Icon icon={flags[record.localeValue] || 'flag'}>
                    {getLocaleLabel(record.localeValue, intl)}
                  </Icon>
                </NavListItem>
              ))}
            </NavListSection>
          </NavList>
        )}
      </Pane>

      {selectedRecord && (
        <LanguagesTranslatorsDetail
          key={selectedRecord.id}
          record={selectedRecord}
          usersMap={usersMap}
          onSave={handleSave}
          onUpdateUsersMap={(newUser) =>
            setUsersMap((prev) => ({ ...prev, [newUser.id]: newUser }))
          }
          onClose={handleClose}
        />
      )}

      <AssignTranslatorModal
        open={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onSave={handleAssignSave}
        languagesList={entryList}
      />
    </Paneset>
  );
}

LanguagesTranslatorsManager.propTypes = {
  match: PropTypes.shape({ path: PropTypes.string.isRequired }).isRequired,
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string.isRequired }).isRequired,
};

export default LanguagesTranslatorsManager;
